import { createSlice } from "@reduxjs/toolkit";
import {
    createCalendarEvent,
    createStudyPlan,
    createStudySession,
    deleteCalendarEvent,
    generateStudyPlan,
    getPlannerStats,
    getStudyPlan,
    listCalendarEvents,
    listStudySessions,
    updateCalendarEvent,
    updateStudyPlan,
} from "../thunks/studyPlanner";

const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.results)) return payload.data.results;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.sessions)) return payload.sessions;
    if (Array.isArray(payload?.events)) return payload.events;
    if (Array.isArray(payload?.calendar_events)) return payload.calendar_events;
    return [];
};

const normalizeItem = (payload) => {
    if (!payload) return payload;
    if (payload.data && !Array.isArray(payload.data)) return payload.data;
    if (payload.session) return payload.session;
    if (payload.study_session) return payload.study_session;
    if (payload.event) return payload.event;
    if (payload.calendar_event) return payload.calendar_event;
    return payload;
};

const getItemId = (item) => item?.id ?? item?._id ?? item?.event_id ?? item?.session_id;
const sameItemId = (left, right) => String(left ?? "") === String(right ?? "");
const hasSessionFields = (item) =>
    Boolean(item?.date || item?.session_date || item?.duration_minutes || item?.minutes || item?.notes);
const hasEventFields = (item) =>
    Boolean(item?.date || item?.event_date || item?.title || item?.name || item?.color);

const studyPlannerSlice = createSlice({
    name: "studyPlanner",
    initialState: {
    studyPlan: null,
    studySessions: [],
    plannerStats: null,
    calendarEvents: [],
    planSummary: null,
    planFocusAreas: [],
    planTips: [],

    createdStudySession: null,
    createdCalendarEvent: null,

    loading: {
        studyPlan: false,
        sessions: false,
        stats: false,
        calendar: false,
        updatePlan: false,
    },

    error: null,
},
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getStudyPlan.pending, (state) => {
                state.loading.studyPlan = true;
                state.error = null;
            })
            .addCase(getStudyPlan.fulfilled, (state, action) => {
                state.loading.studyPlan = false;
                state.studyPlan = normalizeItem(action.payload);
            })
            .addCase(getStudyPlan.rejected, (state, action) => {
                state.loading.studyPlan = false;
                state.error = action.payload?.detail || "Failed to load study plan.";
            })
            .addCase(createStudyPlan.pending, (state) => {
                state.loading.studyPlan = true;
                state.error = null;
            })
            .addCase(createStudyPlan.fulfilled, (state, action) => {
                state.loading.studyPlan = false;
                state.studyPlan = normalizeItem(action.payload);
            })
            .addCase(createStudyPlan.rejected, (state, action) => {
                state.loading.studyPlan = false;
                state.error = action.payload?.detail || "Failed to create study plan.";
            })
            .addCase(generateStudyPlan.pending, (state) => {
                state.loading.studyPlan = true;
                state.error = null;
            })
            .addCase(generateStudyPlan.fulfilled, (state, action) => {
                state.loading.studyPlan = false;
                state.studyPlan = normalizeItem(action.payload?.plan || action.payload);
                if (Array.isArray(action.payload?.calendar_events)) {
                    state.calendarEvents = action.payload.calendar_events;
                }
                state.planSummary = action.payload?.summary || null;
                state.planFocusAreas = action.payload?.focus_areas || [];
                state.planTips = action.payload?.tips || [];
            })
            .addCase(generateStudyPlan.rejected, (state, action) => {
                state.loading.studyPlan = false;
                state.error = action.payload?.error || action.payload?.detail || "Failed to generate study plan.";
            })
            .addCase(updateStudyPlan.pending, (state) => {
                state.loading.updatePlan = true;
                state.error = null;
            })
            .addCase(updateStudyPlan.fulfilled, (state, action) => {
                state.loading.updatePlan = false;
                state.studyPlan = normalizeItem(action.payload);
            })
            .addCase(updateStudyPlan.rejected, (state, action) => {
                state.loading.updatePlan = false;
                state.error = action.payload?.detail || "Failed to update study plan.";
            })
            .addCase(listStudySessions.pending, (state) => {
                state.loading.sessions = true;
                state.error = null;
            })
            .addCase(listStudySessions.fulfilled, (state, action) => {
                state.loading.sessions = false;
                state.studySessions = normalizeList(action.payload);
            })
            .addCase(listStudySessions.rejected, (state, action) => {
                state.loading.sessions = false;
                state.error = action.payload?.detail || "Failed to load study sessions.";
            })
            .addCase(createStudySession.pending, (state) => {
                state.loading.sessions = true;
                state.error = null;
            })
            .addCase(createStudySession.fulfilled, (state, action) => {
                state.loading.sessions = false;
                const createdSession = normalizeItem(action.payload);
                state.createdStudySession = createdSession;
                if (createdSession && (getItemId(createdSession) || hasSessionFields(createdSession))) {
                    state.studySessions = [createdSession, ...state.studySessions];
                }
            })
            .addCase(createStudySession.rejected, (state, action) => {
                state.loading.sessions = false;
                state.error = action.payload?.detail || "Failed to create study session.";
            })
            .addCase(getPlannerStats.pending, (state) => {
                state.loading.stats = true;
                state.error = null;
            })
            .addCase(getPlannerStats.fulfilled, (state, action) => {
                state.loading.stats = false;
                state.plannerStats = action.payload;
            })
            .addCase(getPlannerStats.rejected, (state, action) => {
                state.loading.stats = false;
                state.error = action.payload?.detail || "Failed to load planner stats.";
            })
            .addCase(listCalendarEvents.pending, (state) => {
                state.loading.calendar = true;
                state.error = null;
            })
            .addCase(listCalendarEvents.fulfilled, (state, action) => {
                state.loading.calendar = false;
                state.calendarEvents = normalizeList(action.payload);
            })
            .addCase(listCalendarEvents.rejected, (state, action) => {
                state.loading.calendar = false;
                state.error = action.payload?.detail || "Failed to load calendar events.";
            })
            .addCase(createCalendarEvent.pending, (state) => {
                state.loading.createCalendarEvent = true;
                state.error = null;
            })
            .addCase(createCalendarEvent.fulfilled, (state, action) => {
                state.loading.createCalendarEvent = false;
                const createdEvent = normalizeItem(action.payload);
                state.createdCalendarEvent = createdEvent;
                if (createdEvent && (getItemId(createdEvent) || hasEventFields(createdEvent))) {
                    state.calendarEvents = [createdEvent, ...state.calendarEvents];
                }
            })
            .addCase(createCalendarEvent.rejected, (state, action) => {
                state.loading.createCalendarEvent = false;
                state.error = action.payload?.detail || "Failed to create calendar event.";
            })
            .addCase(updateCalendarEvent.pending, (state) => {
                state.loading.updateCalendarEvent = true;
                state.error = null;
            })
            .addCase(updateCalendarEvent.fulfilled, (state, action) => {
                state.loading.updateCalendarEvent = false;
                const updatedEvent = normalizeItem(action.payload);
                state.calendarEvents = state.calendarEvents.map((event) =>
                    sameItemId(getItemId(event), getItemId(updatedEvent)) ? updatedEvent : event
                );
            })
            .addCase(updateCalendarEvent.rejected, (state, action) => {
                state.loading.updateCalendarEvent = false;
                state.error = action.payload?.detail || "Failed to update calendar event.";
            })
            .addCase(deleteCalendarEvent.pending, (state) => {
                state.loading.deleteCalendarEvent = true;
                state.error = null;
            })
            .addCase(deleteCalendarEvent.fulfilled, (state, action) => {
                state.loading.deleteCalendarEvent = false;
                state.calendarEvents = state.calendarEvents.filter(
                    (event) => !sameItemId(getItemId(event), action.payload.eventId)
                );
            })
            .addCase(deleteCalendarEvent.rejected, (state, action) => {
                state.loading.deleteCalendarEvent = false;
                state.error = action.payload?.detail || "Failed to delete calendar event.";
            });
    },
});

export default studyPlannerSlice.reducer;
